﻿import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Search, X } from "lucide-react";
import { NotFoundIcon } from "@/components/icons";
import { ClearButton } from "@/components/ui/ClearButton";

export interface LookupSelectOption {
  value: string;
  label: string;
}

export interface LookupSelectProps {
  value?: string[];
  onChange: (tags: string[]) => void;
  placeholder?: string;
  onClear?: () => void;
  error?: boolean;
  maxTags?: number;
  fetchSuggestions?: (query: string) => Promise<LookupSelectOption[]>;
  suggestionDebounce?: number;
  noOptionsMessage?: string;
  loadingMessage?: string;
  dropdownPortalId?: string;
}

export const LookupSelect: React.FC<LookupSelectProps> = ({
  value = [],
  onChange,
  onClear,
  error,
  placeholder,
  maxTags = 10,
  fetchSuggestions,
  suggestionDebounce = 250,
  noOptionsMessage = "No records found. Please try a different search.",
  loadingMessage = "Loading...",
  dropdownPortalId
}) => {
  const [inputValue, setInputValue] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const [suggestions, setSuggestions] = useState<LookupSelectOption[]>([]);
  const [optionLabels, setOptionLabels] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const fetchDelayRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const requestIdRef = useRef(0);
  const [dropdownStyles, setDropdownStyles] = useState<React.CSSProperties>();
  const dropdownContentRef = useRef<HTMLDivElement | null>(null);
  const assignDropdownContentRef = useCallback((node: HTMLDivElement | null) => {
    dropdownContentRef.current = node;
  }, []);

  const dropdownPortalElement = useMemo(() => {
    if (typeof document === "undefined") return null;
    if (dropdownPortalId) {
      const element = document.getElementById(dropdownPortalId);
      if (element) return element;
    }

    return document.body;
  }, [dropdownPortalId]);

  const limitReached = value.length >= maxTags;

  const upsertOptionLabels = useCallback((options: LookupSelectOption[]) => {
    setOptionLabels((prev) => {
      let next: Record<string, string> | null = null;
      options.forEach((opt) => {
        if (prev[opt.value] === opt.label) return;
        if (!next) {
          next = { ...prev };
        }
        next[opt.value] = opt.label;
      });
      return next ?? prev;
    });
  }, []);

  const addTag = useCallback(
    (val: string) => {
      const trimmed = val.trim();
      if (!trimmed) return;
      if (value.includes(trimmed)) return;
      if (value.length >= maxTags) return;

      onChange([...value, trimmed]);
      setInputValue("");
      setSuggestions([]);
      setIsDropdownOpen(false);
    },
    [value, onChange, maxTags]
  );

  const removeTag = useCallback(
    (index: number) => {
      if (index < 0) return;
      const newTags = value.filter((_, i) => i !== index);
      onChange(newTags);
    },
    [value, onChange]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter" || e.key === ",") {
        e.preventDefault();
      }
      if (e.key === "Backspace" && !inputValue) {
        removeTag(value.length - 1);
      }
    },
    [inputValue, removeTag, value.length]
  );

  const handleClear = useCallback(() => {
    setInputValue("");
    setSuggestions([]);
    setIsDropdownOpen(false);
    setFetchError(null);
    if (onClear) onClear();
  }, [onClear]);

  const handleSuggestionSelect = useCallback(
    (option: LookupSelectOption) => {
      upsertOptionLabels([option]);
      addTag(option.value);
      inputRef.current?.focus();
      setTimeout(() => {
        inputRef.current?.scrollIntoView({ behavior: "smooth" });
      }, 100);
    },
    [addTag, upsertOptionLabels]
  );

  const updateDropdownPosition = useCallback(() => {
    if (!dropdownPortalElement || !containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    setDropdownStyles({
      position: "absolute",
      top: rect.bottom + window.scrollY + 4,
      left: rect.left + window.scrollX,
      width: rect.width,
      zIndex: 50
    });
  }, [dropdownPortalElement]);

  useEffect(() => {
    if (!fetchSuggestions) return;

    if (fetchDelayRef.current) {
      clearTimeout(fetchDelayRef.current);
    }

    const query = inputValue.trim();

    if (!query) {
      setSuggestions([]);
      setIsDropdownOpen(false);
      setLoading(false);
      setFetchError(null);
      return;
    }

    setLoading(true);
    setFetchError(null);
    const requestId = ++requestIdRef.current;

    fetchDelayRef.current = setTimeout(() => {
      fetchSuggestions(query)
        .then((items) => {
          if (requestIdRef.current !== requestId) return;
          setSuggestions(items);
          upsertOptionLabels(items);
          setIsDropdownOpen(true);
        })
        .catch((err) => {
          if (requestIdRef.current !== requestId) return;
          console.error("Failed to fetch lookup options", err);
          setSuggestions([]);
          setIsDropdownOpen(false);
          setFetchError("Failed to load options");
        })
        .finally(() => {
          if (requestIdRef.current === requestId) {
            setLoading(false);
          }
        });
    }, suggestionDebounce);

    return () => {
      if (fetchDelayRef.current) {
        clearTimeout(fetchDelayRef.current);
      }
    };
  }, [inputValue, fetchSuggestions, suggestionDebounce, upsertOptionLabels]);

  useEffect(() => {
    const handleDocumentClick = (event: MouseEvent) => {
      const target = event.target as Node;
      if (containerRef.current?.contains(target)) return;
      if (dropdownContentRef.current?.contains(target)) return;
      setIsDropdownOpen(false);
    };

    document.addEventListener("mousedown", handleDocumentClick);
    return () => {
      document.removeEventListener("mousedown", handleDocumentClick);
    };
  }, []);

  useEffect(() => {
    if (!limitReached) return;
    setIsDropdownOpen(false);
  }, [limitReached]);

  useEffect(() => {
    if (!dropdownPortalElement) return;
    if (!isDropdownOpen) return;

    updateDropdownPosition();

    const handleReposition = () => updateDropdownPosition();

    window.addEventListener("resize", handleReposition);
    window.addEventListener("scroll", handleReposition, true);

    return () => {
      window.removeEventListener("resize", handleReposition);
      window.removeEventListener("scroll", handleReposition, true);
    };
  }, [dropdownPortalElement, isDropdownOpen, updateDropdownPosition]);

  const resolvedPlaceholder = placeholder ?? "Select";
  const showDropdown = isDropdownOpen && !fetchError;

  const dropdownContent = (
    <div className="overflow-hidden rounded-md border border-gray-200 bg-white shadow-lg">
      {loading ?
        <div className="px-3 py-2 text-sm text-inherit">{loadingMessage}</div>
      : suggestions.length === 0 ?
        <div className="flex flex-col items-center justify-center px-3 py-4 text-sm text-inherit text-center">
          <NotFoundIcon className="w-full max-w-[80px] h-auto" />
          <span className="pt-3">{noOptionsMessage}</span>
        </div>
      : <ul className="max-h-48 overflow-y-auto py-1">
          {suggestions.map((option) => {
            const disabled = value.includes(option.value) || limitReached;
            return (
              <li key={option.value}>
                <button
                  type="button"
                  className={`flex w-full items-center justify-between px-3 py-2 text-left text-sm transition hover:bg-gray-100
                  ${disabled ? "cursor-not-allowed text-gray-400" : "text-gray-700"}`}
                  onClick={() => handleSuggestionSelect(option)}
                  disabled={disabled}
                >
                  <span className="truncate">{option.label}</span>
                </button>
              </li>
            );
          })}
        </ul>
      }
    </div>
  );

  const renderDropdown = () => {
    if (!showDropdown) return null;

    if (dropdownPortalElement && dropdownStyles) {
      return createPortal(
        <div ref={assignDropdownContentRef} style={dropdownStyles}>
          {dropdownContent}
        </div>,
        dropdownPortalElement
      );
    }

    return (
      <div ref={assignDropdownContentRef} className="absolute left-0 right-0 top-full z-10 mt-1">
        {dropdownContent}
      </div>
    );
  };

  return (
    <div className="relative w-full" ref={containerRef}>
      <div
        className={`flex min-h-[36px] items-center gap-3 rounded-md border bg-white px-3 transition-all 
    ${error ? "border-red-500 focus-within:ring-red-500" : "border-gray-300 "}`}
      >
        <div className="flex flex-1 flex-wrap content-start gap-1 overflow-y-auto max-h-[72px] hide-scrollbar pr-4">
          {value.map((tag, i) => {
            const label = optionLabels[tag] ?? tag;
            return (
              <span
                key={`${tag}-${i}`}
                className="flex items-center gap-1 rounded-lg border border-[#f0f0f0] bg-[#f9f9f9] px-2 py-1 my-1 text-sm text-inherit max-w-full break-words"
              >
                <span className="whitespace-pre-wrap break-all">{label}</span>
                <button
                  type="button"
                  onClick={() => removeTag(i)}
                  className="text-inherit transition hover:text-red-500"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            );
          })}
          {!limitReached && (
            <input
              ref={inputRef}
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              onFocus={() => {
                if (suggestions.length > 0 && !fetchError) {
                  setIsDropdownOpen(true);
                }
              }}
              placeholder={(value.length === 0 && resolvedPlaceholder) || ""}
              className="min-w-[120px] flex-1 border-none bg-transparent py-1 text-sm text-gray-600 placeholder:text-gray-400 outline-none"
              disabled={limitReached}
            />
          )}
        </div>

        <div className="flex justify-end items-center gap-2 pointer-events-auto h-fit">
          {value.length === 0 ?
            <span className="flex h-7 w-7 items-center justify-center text-inherit">
              <Search className="h-4 w-4" />
            </span>
          : <ClearButton
              onClick={handleClear}
              className="flex h-7 w-7 items-center justify-center rounded-full text-inherit transition hover:text-red-500"
              aria-label="Clear selected values"
            />
          }
        </div>
      </div>

      {renderDropdown()}

      {fetchError && <p className="mt-1 text-xs text-red-600">{fetchError}</p>}
      {limitReached && !fetchError && maxTags !== 1 && (
        <p className="mt-1 text-xs text-inherit">Maximum {maxTags} tags reached.</p>
      )}
    </div>
  );
};
